import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import type { Agent, AttachAgentMcpServersResponse } from '@/lib/types';
import { ApiError } from '@/lib/api';
import { useAttachAgentMcpServers } from '@/hooks/agents/useAgents';

/**
 * Well-known id of the tool-servers plugin attachment (owned by plugin-mcp).
 * Matches `TOOL_SERVERS_ATTACHMENT_ID` on the server and the id plugin-mcp's
 * own web components use.
 */
export const MCP_SERVERS_ATTACHMENT_ID = 'mcp-servers';

/**
 * Agent statuses from which MCP servers may be attached to the live run.
 * Mirrors the server's `MCP_ATTACHABLE_STATUSES`: the attach pushes
 * registration onto the devbox immediately, so `starting` (the launch path
 * owns boot registration) and every terminal status are excluded.
 */
const ATTACHABLE_STATUSES = new Set<Agent['status']>([
  'running',
  'needs_input',
  'interrupted',
  'completed',
]);

/**
 * Human copy for the machine-readable codes `attachMcpServers` throws.
 * Mirrors `SWITCH_ERROR_COPY` in `useSubscriptionSwitch`.
 */
const ATTACH_ERROR_COPY: Record<string, string> = {
  agent_devbox_not_running:
    'The sandbox is not running right now. Send the agent a message to wake it, then try again.',
  agent_not_attachable: 'This run has finished, so MCP servers can no longer be attached.',
  mcp_config_not_found: 'That MCP server is unavailable or disabled.',
  mcp_live_attach_unsupported:
    'This agent type does not support attaching MCP servers while running.',
  mcp_registration_failed:
    'The sandbox could not register the MCP server. Nothing was attached; try again.',
  sandbox_provider_unavailable: 'The sandbox provider is not reachable right now. Try again.',
  no_active_organization: 'Pick an organization before attaching MCP servers.',
};

/**
 * Success copy for an attach, keyed on whether the agent process that is
 * running right now loaded the servers.
 *
 * The distinction is the whole point of reporting activation: a `deferred`
 * attach leaves the user staring at an agent that cannot see the server they
 * just picked, so say so instead of promising it on the next turn.
 *
 * Exported for tests; the hook is the only production caller.
 */
export function attachMcpSuccessMessage(
  activation: AttachAgentMcpServersResponse['activation'],
  count: number,
): string {
  const one = count === 1;
  const subject = one ? 'MCP server attached' : `${count} MCP servers attached`;
  const them = one ? 'it' : 'them';
  if (activation.state === 'active') {
    return `${subject}. The agent can use ${them} on its next turn.`;
  }
  if (activation.reason === 'agent-unreachable') {
    return `${subject}, but the running agent did not load ${them}. Restart the session to pick ${them} up.`;
  }
  return `${subject}. This agent loads MCP servers at startup, so ${one ? 'it becomes' : 'they become'} available when the session next restarts.`;
}

/** A bare `snake_case`/`SCREAMING_CASE` identifier, never product copy. */
const CODE_LIKE = /^[A-Za-z0-9_]+$/;

/**
 * Toast copy for a failed attach. Prefers the mapped code; falls back to the
 * server's own message when it is a real sentence (the live-attach errors for
 * local servers carry a useful explanation), and to a generic line rather
 * than leaking an identifier into the UI.
 *
 * Exported for tests; the hook is the only production caller.
 */
export function attachMcpErrorMessage(err: unknown): string {
  if (!(err instanceof Error)) return 'Could not attach the MCP server. Try again.';
  const code = err instanceof ApiError ? (err.code ?? err.message) : err.message;
  const mapped = ATTACH_ERROR_COPY[code];
  if (mapped) return mapped;
  if (err.message && !CODE_LIKE.test(err.message)) return err.message;
  return 'Could not attach the MCP server. Try again.';
}

export interface McpServerAttach {
  /** True when this agent can take a live MCP attach right now. */
  canAttach: boolean;
  /** Ids already on the run's `mcp-servers` attachment. */
  appliedIds: string[];
  /**
   * Attach the servers named by a mention result's attachment config
   * (`{ mcpServerIds }` holding the full desired list). Ids already attached
   * are subtracted here, so applying a provider row that re-sends the whole
   * list only pushes the new server.
   */
  onAttach: (attachmentConfig: unknown) => void;
  /** True while an attach is in flight. */
  pending: boolean;
}

/**
 * Shared wiring for attaching MCP servers to a running agent from the chat
 * composer's `@` menu (and its "+" trigger). Mirrors `useSubscriptionSwitch`:
 * the mutation seeds the agent cache, and the toasts translate the server's
 * machine codes into product copy. Hooks are called unconditionally so this
 * is safe to invoke before gating on {@link McpServerAttach.canAttach}.
 */
export function useMcpServerAttach(agent: Agent): McpServerAttach {
  const attach = useAttachAgentMcpServers();

  const appliedIds = useMemo<string[]>(() => {
    const entry = agent.attachments?.find((a) => a.attachmentId === MCP_SERVERS_ATTACHMENT_ID);
    const ids = (entry?.config as { mcpServerIds?: unknown } | undefined)?.mcpServerIds;
    return Array.isArray(ids) ? ids.filter((v): v is string => typeof v === 'string') : [];
  }, [agent.attachments]);

  const onAttach = useCallback(
    (attachmentConfig: unknown) => {
      // One attach at a time: the request spans a devbox exec (seconds), and
      // a second pick racing the first could re-register a server the box
      // already has — append-style provider configs would duplicate it.
      if (attach.isPending) return;
      const requested = (attachmentConfig as { mcpServerIds?: unknown } | undefined)?.mcpServerIds;
      const ids = Array.isArray(requested)
        ? requested.filter((v): v is string => typeof v === 'string')
        : [];
      const added = ids.filter((id) => !appliedIds.includes(id));
      if (added.length === 0) return;
      attach.mutate(
        { id: agent.id, mcpServerIds: added },
        {
          onSuccess: (result) =>
            toast.success(attachMcpSuccessMessage(result.activation, added.length)),
          onError: (err) => toast.error(attachMcpErrorMessage(err)),
        },
      );
    },
    [agent.id, appliedIds, attach],
  );

  return {
    canAttach: Boolean(agent.devboxId) && ATTACHABLE_STATUSES.has(agent.status),
    appliedIds,
    onAttach,
    pending: attach.isPending,
  };
}
